import type { CSSProperties } from "react";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";

type ProcessorType = "unit" | "direct" | "custom";

interface BaseProperty {
  cssProperty: keyof CSSProperties;
  processorType: ProcessorType;
}

interface UnitProperty extends BaseProperty {
  cssProperty: "fontSize" | "lineHeight" | "borderWidth" | "opacity";
  defaultUnit: string;
  processorType: "unit";
}

interface DirectProperty extends BaseProperty {
  cssProperty:
    | "color"
    | "textAlign"
    | "textTransform"
    | "textDecoration"
    | "fontStyle"
    | "borderColor"
    | "backgroundColor"
    | "width"
    | "minWidth"
    | "maxWidth"
    | "minHeight"
    | "maxHeight"
    | "height"
    | "listStyleType"
    | "listStylePosition"
    | "objectFit"
    | "objectPosition";
  processorType: "direct";
}

interface CustomProperty extends BaseProperty {
  cssProperty:
    | "fontWeight"
    | "borderRadius"
    | "padding"
    | "margin"
    | "backgroundImage";
  processor: (value: StyleValue) => string | number | undefined;
  processorType: "custom";
}

interface BorderRadiusValue {
  border_left_bottom_radius: number;
  border_left_top_radius: number;
  border_right_bottom_radius: number;
  border_right_top_radius: number;
}

export type StyleDefinition = UnitProperty | DirectProperty | CustomProperty;

export const styleMap: Record<string, StyleDefinition> = {
  color: {
    cssProperty: "color",
    processorType: "direct",
  },
  textTransform: {
    cssProperty: "textTransform",
    processorType: "direct",
  },
  fontStyle: {
    cssProperty: "fontStyle",
    processorType: "direct",
  },
  fontSize: {
    cssProperty: "fontSize",
    processorType: "unit",
    defaultUnit: "rem",
  },
  lineHeight: {
    cssProperty: "lineHeight",
    processorType: "unit",
    defaultUnit: "",
  },
  fontWeight: {
    cssProperty: "fontWeight",
    processorType: "custom",
    processor: value => {
      const val = parseInt(String(value.value), 10);
      return isNaN(val) ? String(value.value) : val;
    },
  },
  textAlign: {
    cssProperty: "textAlign",
    processorType: "direct",
  },
  textDecoration: {
    cssProperty: "textDecoration",
    processorType: "direct",
  },
  borderRadius: {
    cssProperty: "borderRadius",
    processorType: "custom",
    processor: value => {
      if (typeof value.value === "object") {
        const corners = value.value as BorderRadiusValue;

        return `${corners.border_left_top_radius}px ${corners.border_right_top_radius}px ${corners.border_right_bottom_radius}px ${corners.border_left_bottom_radius}px`;
      }
      return typeof value.value === "number" ? `${value.value}px` : undefined;
    },
  },
  borderWidth: {
    cssProperty: "borderWidth",
    processorType: "unit",
    defaultUnit: "px",
  },
  borderColor: {
    cssProperty: "borderColor",
    processorType: "direct",
  },
  backgroundColor: {
    cssProperty: "backgroundColor",
    processorType: "direct",
  },
  backgroundImage: {
    cssProperty: "backgroundImage",
    processorType: "custom",
    processor: value => {
      if (typeof value.value === "string" && Boolean(value.value)) {
        const val = value.value.trim();
        if (val.includes("gradient(")) {
          return val;
        }
        return `url(${val})`;
      }
      return undefined;
    },
  },
  padding: {
    cssProperty: "padding",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        paddingTop?: number | string;
        paddingBottom?: number | string;
        paddingLeft?: number | string;
        paddingRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { paddingTop, paddingBottom, paddingLeft, paddingRight } = val;

        return `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
      }

      return undefined;
    },
  },
  margin: {
    cssProperty: "margin",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        marginTop?: number | string;
        marginBottom?: number | string;
        marginLeft?: number | string;
        marginRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { marginTop, marginBottom, marginLeft, marginRight } = val;
        return `${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`;
      }

      return undefined;
    },
  },
  width: {
    cssProperty: "width",
    processorType: "direct",
  },
  height: {
    cssProperty: "height",
    processorType: "direct",
  },
  minWidth: {
    cssProperty: "minWidth",
    processorType: "direct",
  },
  maxWidth: {
    cssProperty: "maxWidth",
    processorType: "direct",
  },
  minHeight: {
    cssProperty: "minHeight",
    processorType: "direct",
  },
  maxHeight: {
    cssProperty: "maxHeight",
    processorType: "direct",
  },
  listStyleType: {
    cssProperty: "listStyleType",
    processorType: "direct",
  },
  listStylePosition: {
    cssProperty: "listStylePosition",
    processorType: "direct",
  },
  objectFit: {
    cssProperty: "objectFit",
    processorType: "direct",
  },
  objectPosition: {
    cssProperty: "objectPosition",
    processorType: "direct",
  },
  opacity: {
    cssProperty: "opacity",
    processorType: "unit",
    defaultUnit: "%",
  },
} as const;
